// @react
import React, { useState, ReactElement } from 'react'
// @libraries
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
// @components
import Grid from '@bit/atd.web.grid'
import Banner from '@bit/atd.web.banner'
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Colors from '@bit/atd.web.colors'
import { PropsType as IconProps } from '@bit/atd.web.icons'
// @module components
import useResizeObserver from 'use-resize-observer'

const useStyles = makeStyles({
  containerOuter: {
    '& > div': {
      borderColor: Colors.gray400,
      backgroundColor: Colors.white,
    },
    '& > div > span svg': {
      height: '24px',
      width: '24px',
    },
  },
  container: {
    '& $header + $actions': {
      paddingTop: 16,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    '& $titleText + $subTitleText': {
      marginTop: 4,
    },
  },
  titleText: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
  },
  subTitleText: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
  },
  actions: {
    '& a + a': {
      marginLeft: '12px',
    },
  },
  isHorizontal: {
    '& $actions': {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    '& $header + $actions': {
      paddingTop: 0,
    },
  },
})

type PropsType = {
  isVisible: boolean
  onBtnClick?: () => void
  spacerSize?: number
  title: string
  description?: string
  btnText: string
  btnLink?: string
  icon: IconProps['glyph']
}

const InfoBanner = (props: PropsType): ReactElement => {
  const {
    isVisible,
    onBtnClick,
    spacerSize = 0,
    icon,
    title,
    description,
    btnText,
    btnLink,
  } = props

  /**********************
   * REFS
   **********************/

  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize: ({ width }) => {
      if (width && width > 840) {
        setIsHoritonztal(true)
      } else {
        setIsHoritonztal(false)
      }
    },
  })

  /**********************
   * STATE
   **********************/

  const [isHorizontal, setIsHoritonztal] = useState(false)

  /**********************
   * HOOKS
   **********************/

  const classes = useStyles()

  /**********************
   * RENDER
   **********************/

  return (
    <Grid container>
      <Grid item xs={12}>
        <div ref={ref} style={{ fontSize: 0 }}>
          {isVisible ? (
            <div className={classes.containerOuter}>
              <Banner fullWidth icon={icon}>
                <Grid
                  container
                  className={clsx(classes.container, {
                    [classes.isHorizontal]: isHorizontal,
                  })}
                >
                  {/* Header */}
                  <Grid
                    item
                    xs={isHorizontal ? 8 : 12}
                    className={classes.header}
                  >
                    <div className={classes.titleText}>{title}</div>
                    <div className={classes.subTitleText}>{description}</div>
                  </Grid>

                  {/* Actions */}
                  <Grid
                    item
                    xs={isHorizontal ? 4 : 12}
                    className={classes.actions}
                  >
                    <Button
                      size={'small'}
                      href={btnLink}
                      onClick={() => {
                        if (onBtnClick) {
                          onBtnClick()
                        }
                      }}
                    >
                      {btnText}
                    </Button>
                  </Grid>
                </Grid>
              </Banner>
            </div>
          ) : (
            <Spacer size={spacerSize} />
          )}
        </div>
      </Grid>
    </Grid>
  )
}

export default InfoBanner
